/**
 * Feldfrüchte — explainer page describing how Bodenblick "sees" crops
 * from space. Public, no auth.
 *
 * Route: /feldfruechte
 *
 * Structure: scrollable narrative
 *   1. Hero: "Wie erkennt man Feldfrüchte aus dem All?"
 *   2. NDVI primer (one big illustrated metric card)
 *   3. Annual curve — different crops, different fingerprints
 *   4. Side-by-side: photo + satellite mock + classification
 *   5. Limits & confidence
 *   6. Try-it CTAs (dashboard, planner)
 */
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ArrowLeft, Leaf, Plant, Eye, ChartLineUp, FlowerLotus,
  Sparkle, Question, Sun, Drop, Cloud, ArrowRight, Compass, Grains,
} from "@phosphor-icons/react";
import { StoryCard, ExplainPanel, MetricTooltip } from "@/components/Explain";
import GlossaryTerm from "@/components/GlossaryTerm";

// Crop "fingerprints" — typical NDVI curve points across the year.
// Hand-curated approximations for Central-EU climate, just for illustration.
const CROPS = [
  {
    id: "weizen", label: "Winterweizen", color: "#C8A35A",
    Icon: Grains,
    description: "Sät im Oktober. Schläft im Winter (NDVI ≈ 0.3). Schnellst-Wachstum im April-Mai bis ≈ 0.85. Erntet im Juli.",
    curve: [0.15, 0.20, 0.32, 0.48, 0.65, 0.82, 0.83, 0.70, 0.42, 0.25, 0.20, 0.15],
  },
  {
    id: "mais", label: "Mais", color: "#FFC24B",
    Icon: Plant,
    description: "Sät im Mai. Steile Rampe Juni-August bis NDVI ≈ 0.9. Reift September. Charakteristisch: später Start, hoher Peak.",
    curve: [null, null, null, null, 0.18, 0.42, 0.78, 0.88, 0.78, 0.42, null, null],
  },
  {
    id: "raps", label: "Winterraps", color: "#F0C842",
    Icon: FlowerLotus,
    description: "Sät im August. Markante Blüh-Welle April-Mai (NDVI-Plateau wegen Blütenmeer). Erntet Juli.",
    curve: [0.30, 0.30, 0.45, 0.68, 0.80, 0.55, 0.30, null, 0.18, 0.30, 0.35, 0.30],
  },
  {
    id: "gruenland", label: "Grünland (Mehrschnitt)", color: "#7EE8B0",
    Icon: Leaf,
    description: "Drei Schnitte pro Jahr: Mai, Juli, September. NDVI sägezahn-förmig, fällt jedes Mal kurz ab.",
    curve: [0.30, 0.35, 0.55, 0.75, 0.40, 0.78, 0.45, 0.75, 0.45, 0.55, 0.40, 0.35],
  },
];

const MONTHS = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

function NDVICurve({ values, color }) {
  const W = 360, H = 110, padX = 20, padY = 12;
  const points = values.map((v, i) => {
    if (v == null) return null;
    const x = padX + (i / (values.length - 1)) * (W - 2 * padX);
    const y = H - padY - v * (H - 2 * padY);
    return [x, y];
  });
  // Build segmented polyline (skip null gaps).
  const segments = [];
  let current = [];
  points.forEach((p) => {
    if (p) current.push(p);
    else if (current.length) { segments.push(current); current = []; }
  });
  if (current.length) segments.push(current);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-28">
      {/* Grid lines at NDVI 0.5 and 0.8 */}
      {[0.3, 0.5, 0.8].map((v) => (
        <line key={v}
          x1={padX} x2={W - padX}
          y1={H - padY - v * (H - 2 * padY)}
          y2={H - padY - v * (H - 2 * padY)}
          stroke="#FFFFFF" strokeOpacity="0.05" strokeDasharray="2,3"
        />
      ))}
      {segments.map((seg, i) => (
        <motion.polyline
          key={i}
          points={seg.map((p) => p.join(",")).join(" ")}
          fill="none" stroke={color} strokeWidth="2.5"
          initial={{ pathLength: 0 }}
          whileInView={{ pathLength: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1.4, ease: "easeOut" }}
        />
      ))}
      {/* x-axis labels — short, every other month */}
      {MONTHS.map((m, i) => i % 2 === 0 && (
        <text key={m} x={padX + (i / 11) * (W - 2 * padX)} y={H - 1}
          textAnchor="middle" className="font-mono" fontSize="8" fill="#8A8A93">
          {m}
        </text>
      ))}
      {/* Highlight peak month */}
      {(() => {
        let maxV = -1, maxI = 0;
        values.forEach((v, i) => { if (v != null && v > maxV) { maxV = v; maxI = i; } });
        if (maxV < 0) return null;
        const x = padX + (maxI / (values.length - 1)) * (W - 2 * padX);
        const y = H - padY - maxV * (H - 2 * padY);
        return <circle cx={x} cy={y} r="4" fill={color} stroke="white" strokeWidth="1.5" />;
      })()}
    </svg>
  );
}

export default function Feldfruechte() {
  return (
    <>
      <Helmet>
        <title>Wie erkennt Bodenblick Feldfrüchte? · Bodenblick</title>
        <meta name="description" content="So liest Bodenblick aus Satellitenbildern, ob auf einem Acker Weizen, Mais, Raps oder Grünland wächst — und wann was passiert." />
      </Helmet>
      <div className="min-h-screen bg-[#0A0A0A]" data-testid="feldfruechte-page">
        <header className="border-b border-white/15 bg-[#121212]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
            <Link to="/" className="inline-flex items-center gap-2 text-sm font-medium text-[#00FA9A] hover:underline" data-testid="feldfruechte-back">
              <ArrowLeft size={14} /> <Leaf size={14} weight="fill" /> Bodenblick
            </Link>
            <Link to="/dashboard" className="text-xs text-white/60 hover:text-[#00FA9A] inline-flex items-center gap-1">
              <Compass size={12} /> Zum Cockpit
            </Link>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12 sm:py-16 space-y-12">
          {/* HERO */}
          <section data-testid="feldfruechte-hero" className="relative">
            <div className="absolute inset-0 -z-10 overflow-hidden pointer-events-none" aria-hidden>
              <div className="absolute -top-10 -left-20 w-80 h-80 rounded-full blur-3xl opacity-30" style={{ background: "radial-gradient(circle, #5E8C4A 0%, transparent 65%)" }} />
              <div className="absolute top-32 -right-20 w-72 h-72 rounded-full blur-3xl opacity-20" style={{ background: "radial-gradient(circle, #FFC24B 0%, transparent 65%)" }} />
            </div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-[#00FA9A] font-mono mb-3 inline-flex items-center gap-2">
              <Eye size={12} weight="fill" /> Erklärt · So funktioniert es
            </p>
            <h1 className="font-display text-4xl sm:text-6xl lg:text-[5rem] font-bold tracking-tighter leading-[0.9] bb-accent-stripe">
              Wie erkennt man <span className="text-[#00FA9A]">Feldfrüchte</span> aus dem All?
            </h1>
            <p className="mt-6 text-base sm:text-lg text-white/70 max-w-2xl leading-relaxed">
              Die kurze Antwort: Wir schauen <strong>nicht aufs Foto</strong>, wir
              schauen auf das <strong>Wachstums-Muster</strong>. Jede Feldfrucht hinterlässt
              über das Jahr eine charakteristische <GlossaryTerm word="NDVI">NDVI</GlossaryTerm>-Signatur — und die ist
              fast so einzigartig wie ein Fingerabdruck.
            </p>
          </section>

          {/* NDVI Primer */}
          <StoryCard
            eyebrow="Schritt 1 · NDVI verstehen"
            title="Eine einzige Zahl misst, wie viel Chlorophyll auf einem Acker steckt."
            lead="NDVI heißt Normalized Difference Vegetation Index. Er nutzt aus, dass gesunde Pflanzen rotes Licht schlucken und nahes Infrarot reflektieren. Aus diesem Verhältnis baut der Satellit eine Zahl zwischen –1 und 1."
            bullets={[
              "NDVI 0.1 = nackter Boden oder Stoppeln",
              "NDVI 0.5 = Pflanzen in mittlerer Wuchsphase",
              "NDVI 0.85 = dichter, voll im Saft stehender Bestand",
            ]}
            accent="#00FA9A"
            icon={ChartLineUp}
            testid="feldfruechte-ndvi-card"
          />

          {/* Curves */}
          <section className="space-y-6" data-testid="feldfruechte-curves">
            <header>
              <p className="text-[11px] uppercase tracking-[0.22em] font-mono text-zinc-400">
                Schritt 2 · Jahres-Fingerabdruck
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter mt-2 leading-tight">
                Vier Kulturen, vier sehr unterschiedliche Muster.
              </h2>
              <p className="text-sm text-white/70 mt-2 max-w-2xl leading-relaxed">
                Wir verfolgen NDVI über das ganze Jahr. Wenn ein Acker im Februar bei 0.3
                liegt, im Mai auf 0.85 hochschießt und Ende Juli abrupt zurück auf 0.2 fällt
                — dann war da fast sicher <strong>Winterweizen</strong>. Mais startet später
                und höher. Raps blüht mit einem Plateau. Grünland sägezahnt.
              </p>
            </header>

            <div className="grid sm:grid-cols-2 gap-4">
              {CROPS.map((c) => (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.5 }}
                  className="border border-white/15 bg-[#121212] p-4 sm:p-5"
                  data-testid={`feldfruechte-curve-${c.id}`}
                  style={{ borderLeftWidth: 4, borderLeftColor: c.color }}
                >
                  <div className="flex items-center gap-2.5">
                    <c.Icon size={20} weight="fill" style={{ color: c.color }} />
                    <h3 className="font-display font-bold text-lg tracking-tight">{c.label}</h3>
                  </div>
                  <NDVICurve values={c.curve} color={c.color} />
                  <p className="text-[12px] text-white/70 leading-snug mt-1">{c.description}</p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* Classification process */}
          <section data-testid="feldfruechte-process" className="border border-white/15 bg-[#121212] p-6 sm:p-8">
            <p className="text-[11px] uppercase tracking-[0.22em] font-mono text-zinc-400">
              Schritt 3 · Klassifizierung
            </p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tighter mt-2 leading-tight">
              Drei Datenquellen, ein Algorithmus.
            </h2>
            <div className="grid sm:grid-cols-3 gap-4 mt-5">
              <div className="border-l-2 border-[#00FA9A]/40 pl-3">
                <Sun size={18} weight="fill" className="text-[#00FA9A]" />
                <h3 className="font-display font-bold text-base mt-1">Sentinel-2 Satellit</h3>
                <p className="text-[12px] text-white/70 leading-snug mt-1">
                  ESA-Satellit, alle 5 Tage über Mitteleuropa. 10 m Auflösung, 13 Bänder.
                  Daraus berechnet Bodenblick NDVI, NDWI, GNDVI und 4 weitere Indizes.
                </p>
              </div>
              <div className="border-l-2 border-[#FFC24B]/40 pl-3">
                <Cloud size={18} weight="fill" className="text-[#FFC24B]" />
                <h3 className="font-display font-bold text-base mt-1">Wetter-Kontext</h3>
                <p className="text-[12px] text-white/70 leading-snug mt-1">
                  Open-Meteo (ERA5 + ICON). Temperatursumme seit Jahresanfang („Wärmesumme")
                  — entscheidend, um Mais von Sommergerste zu unterscheiden.
                </p>
              </div>
              <div className="border-l-2 border-[#D9A87C]/40 pl-3">
                <Drop size={18} weight="fill" className="text-[#D9A87C]" />
                <h3 className="font-display font-bold text-base mt-1">Bodenfeuchte</h3>
                <p className="text-[12px] text-white/70 leading-snug mt-1">
                  Sumpf-tolerante Kulturen (Reis, Mais) vs. trocken-tolerante (Gerste).
                  Bodenblick erkennt Wasser-Stress und ordnet ihn der wahrscheinlichen Kultur zu.
                </p>
              </div>
            </div>
            <div className="mt-6 bg-[#0A0A0A] border border-white/15 rounded-sm p-4 text-sm leading-relaxed text-white/80">
              <span className="font-semibold text-[#00FA9A]">Ergebnis:</span> Pro Pixel
              (10×10 m) eine wahrscheinlichste Kultur + Konfidenz-Wert von 0–100%.
              Bei einem ganzen Acker mitteln wir die Pixel — und kommen meist auf
              <MetricTooltip explain="In Studien des Joint Research Centre der EU erreicht die NDVI-basierte Klassifizierung 85–92% Genauigkeit bei den 5 wichtigsten Kulturen in DE/AT.">
                <strong> ≈90% Genauigkeit</strong>
              </MetricTooltip>
              .
            </div>
          </section>

          {/* Limits */}
          <ExplainPanel
            title="Wo stößt Bodenblick an Grenzen?"
            accent="#FF5500"
            testid="feldfruechte-limits"
            defaultOpen={false}
          >
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Wolken</strong> blockieren den Satellit. In Mitteleuropa haben wir typischerweise alle 5–10 Tage einen klaren Blick.</li>
              <li><strong>Mischkulturen</strong> (z. B. Roggen-Wicken-Gemenge) sind schwerer zu unterscheiden.</li>
              <li><strong>Sehr kleine Felder</strong> (&lt;0.5 ha) verlieren durch Pixel-Mittelung an Genauigkeit.</li>
              <li><strong>Glashaus-Anbau</strong> sieht der Satellit nicht — das Dach reflektiert anders als Pflanzen.</li>
            </ul>
            <p className="text-xs text-zinc-400 mt-2 pt-2 border-t border-white/15">
              Für offizielle Anbaustatistiken empfehlen wir den BMEL-Agrarstrukturbericht.
              Bodenblick ergänzt — wir ersetzen nicht.
            </p>
          </ExplainPanel>

          {/* CTAs */}
          <section className="grid sm:grid-cols-2 gap-4">
            <Link
              to="/dashboard"
              className="block border border-[#00FA9A]/40 bg-[#00FA9A] text-black p-5 hover:bg-[#00FA9A]/20 transition-colors group"
              data-testid="feldfruechte-cta-dashboard"
            >
              <Compass size={20} className="opacity-80" />
              <h3 className="font-display font-bold text-xl mt-2">Im Cockpit anwenden</h3>
              <p className="text-sm opacity-80 mt-1 leading-snug">
                Sieh dir an, was Bodenblick gerade über die Felder Mitteleuropas weiß.
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-mono uppercase tracking-widest mt-3 group-hover:gap-2 transition-all">
                Cockpit öffnen <ArrowRight size={12} />
              </span>
            </Link>
            <Link
              to="/planner"
              className="block border border-white/15 bg-[#121212] p-5 hover:bg-white/5 transition-colors group"
              data-testid="feldfruechte-cta-planner"
            >
              <Plant size={20} className="text-[#00FA9A]" />
              <h3 className="font-display font-bold text-xl mt-2 text-white">Ernteplaner ausprobieren</h3>
              <p className="text-sm text-white/70 mt-1 leading-snug">
                Wann ist der nächste gute Tag zum Säen? Bodenblick rechnet's für dich.
              </p>
              <span className="inline-flex items-center gap-1 text-sm font-mono uppercase tracking-widest mt-3 text-[#00FA9A] group-hover:gap-2 transition-all">
                Planer öffnen <ArrowRight size={12} />
              </span>
            </Link>
          </section>

          <p className="text-[11px] text-zinc-400 font-mono text-center pt-4">
            <Question size={11} className="inline mr-1" />
            Fragen? Schreib uns an <a href="mailto:vision@bodenblick.com" className="underline">vision@bodenblick.com</a>
          </p>
        </main>
      </div>
    </>
  );
}
